import type { ConfirmedDate } from '../types';
import { formatDate, isValidDate } from '../utils/formatDate';

type DateVisibility = {
  showExpirationDateWhenAvailable?: boolean;
  showUseByDateWhenAvailable?: boolean;
  showSellByDateWhenAvailable?: boolean;
};

/** Colorado shelf-life dates are mandatory; printed dates (including blanks) remain immutable. */
export function resolveLandingDates(
  coa: Record<string, unknown> | null,
  productCard?: DateVisibility | null,
  confirmedDates?: ConfirmedDate[],
  hideHarvestDate = false,
  marketCode?: string,
): [string, string][] {
  const requiresShelfLifeDates = marketCode?.trim().toUpperCase() === 'CO';
  const shelfLifeLabels = {
    expirationDate: 'Expiration Date',
    useByDate: 'Use By Date',
    sellByDate: 'Sell By Date',
  };
  const visible: Partial<Record<ConfirmedDate['kind'], boolean>> = {
    harvestDate: !requiresShelfLifeDates && !hideHarvestDate,
    expirationDate: requiresShelfLifeDates || !!productCard?.showExpirationDateWhenAvailable,
    useByDate: requiresShelfLifeDates || !!productCard?.showUseByDateWhenAvailable,
    sellByDate: requiresShelfLifeDates || !!productCard?.showSellByDateWhenAvailable,
  };
  const saved = new Map<ConfirmedDate['kind'], ConfirmedDate>();
  for (const date of Array.isArray(confirmedDates) ? confirmedDates : []) {
    if (!date?.kind || (!date.usedByTemplate && !date.isExplicitlyBlank)) continue;
    // One row per kind; even malformed duplicates must not resurrect a deliberate blank.
    if (!saved.has(date.kind) || date.isExplicitlyBlank) saved.set(date.kind, date);
  }

  const rows: [string, string][] = [];
  const productionDate = typeof coa?.productionBatchDate === 'string'
    ? coa.productionBatchDate.trim().split('T')[0] : '';
  const parsedProductionDate = new Date(productionDate);
  if (requiresShelfLifeDates && isValidDate(coa?.productionBatchDate)
    && /^\d{4}-\d{2}-\d{2}$/.test(productionDate) && Number.isFinite(parsedProductionDate.getTime())
    && parsedProductionDate.toISOString().slice(0, 10) === productionDate) {
    rows.push(['Production Batch date', formatDate(productionDate)]);
  }
  for (const date of saved.values()) {
    if (visible[date.kind] === false || date.isExplicitlyBlank) continue;
    const value = formatDate(date.confirmedDate);
    const label = requiresShelfLifeDates && (
      date.kind === 'expirationDate' || date.kind === 'useByDate' || date.kind === 'sellByDate'
    ) ? shelfLifeLabels[date.kind] : date.displayLabel;
    if (value) rows.push([label, value]);
  }

  const harvestSource = isValidDate(coa?.harvestDate) ? coa?.harvestDate : coa?.['harvestDate-v2'];
  const rawDates = coa?.rawPackageDates;
  const packageDate = (kind: string): unknown => {
    if (rawDates && typeof rawDates === 'object' && !Array.isArray(rawDates)) {
      const rawDateRecord = rawDates as Record<string, unknown>;
      const entries = Object.entries(rawDateRecord);
      const findRawDate = (key: string) =>
        entries.find(([candidate]) => candidate === key) ??
        entries.find(([candidate]) => candidate.toLowerCase() === key.toLowerCase());
      const rawEntry = findRawDate(kind);
      if (!rawEntry) return undefined;
      const [rawKey, rawValue] = rawEntry;
      const flatValue = coa?.[kind];
      const rawUseByValue = findRawDate('useByDate')?.[1];
      const validRawValue = typeof rawValue === 'string' && isValidDate(rawValue) ? rawValue : undefined;
      const validFlatValue = typeof flatValue === 'string' && isValidDate(flatValue) ? flatValue : undefined;
      if (!validRawValue) return undefined;

      // A flat date can be a saved package override. Older COAs also used the
      // flat expirationDate field as a Use By alias, so retain the raw expiration
      // only for that legacy shape.
      if (
        kind.toLowerCase() === 'expirationdate' &&
        validFlatValue &&
        validRawValue &&
        isValidDate(rawUseByValue) &&
        formatDate(validFlatValue) === formatDate(rawUseByValue)
      ) {
        return validRawValue;
      }
      if (kind.toLowerCase() === 'expirationdate' && rawKey !== kind) return validRawValue;
      return validFlatValue ?? validRawValue;
    }
    return coa?.[kind];
  };
  const fallback: [ConfirmedDate['kind'], string, unknown][] = [
    ['harvestDate', 'Harvest Date', harvestSource],
    ['packagedDate', 'Packaged On', packageDate('packagedDate')],
    ['expirationDate', shelfLifeLabels.expirationDate, packageDate('expirationDate')],
    ['useByDate', shelfLifeLabels.useByDate, packageDate('useByDate')],
    ['sellByDate', shelfLifeLabels.sellByDate, packageDate('sellByDate')],
  ];
  for (const [kind, label, source] of fallback) {
    // A saved unresolved value is also authoritative; absence is different from blank.
    if (saved.has(kind) || visible[kind] === false) continue;
    const value = formatDate(source);
    if (value) rows.push([label, value]);
  }
  return rows;
}
